interface ToggleSwitch {
  value: Boolean,
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  labelText: string,
  description?: string,
  disabled?: boolean,
  infoText?: string | null
}

/**
 * A switch, not a Yes/No segmented pair.
 *
 * The old control was a 86×28 box with the two words inside it, which read as
 * a pair of buttons where only one was ever pressed. A switch says the same
 * thing in a shape people already know, and the question moves to one line
 * beside it. `infoText` renders as an inline hint rather than a tooltip.
 */
const ToggleSwitchCheckbox = ({ value, onChange, labelText, description, disabled, infoText }: ToggleSwitch) => {
  // The reason wins over the description, and only when there is one.
  //
  // It used to be the other way round, which meant a switch nobody could move
  // explained the feature and never said why it was grey: both call sites pass
  // a description AND pass `infoText` only when the control is unavailable, so
  // the one line that mattered was the one that could never render. A disabled
  // control that gives no reason reads as broken, not as locked.
  const hint = infoText || description;

  return (
    <div className={`flex items-start justify-between gap-4 ${disabled ? 'opacity-50' : ''}`}>
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <span className="text-ds-135 font-medium leading-[1.4] text-white">{labelText}</span>
        {hint ? <span className="text-ds-115 leading-[1.5] text-navy-200">{hint}</span> : null}
      </div>
      <label
        className={`relative flex-none w-[46px] h-[26px] rounded-full border p-0.5 flex items-center transition-colors duration-[120ms] ${
          disabled ? 'cursor-not-allowed' : 'cursor-pointer'
        } ${value ? 'justify-end bg-state-ok border-state-ok' : 'justify-start bg-navy-600 border-navy-400'}`}
      >
        <input
          type="checkbox"
          className="sr-only peer"
          checked={!!value}
          onChange={onChange}
          disabled={disabled}
          role="switch"
          aria-label={labelText}
        />
        <span className="w-5 h-5 rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,.4)] block peer-focus-visible:ring-2 peer-focus-visible:ring-action peer-focus-visible:ring-offset-1" />
      </label>
    </div>
  )
}

export default ToggleSwitchCheckbox;
